'use client'

import { compact } from 'lodash'
import {
  BellDot,
  BookOpen,
  Coins,
  Fingerprint,
  Fuel,
  Gem,
  Globe,
  KeyRound,
  Landmark,
  Puzzle,
  Settings,
  Shield,
  User,
  Users,
} from 'lucide-react'

import { NotificationDotLabel, SidebarSection } from '~/components/sidebar'
import { useActiveOrg, useActiveOrgMember } from '~/hooks/org'
import { useShowExtensionsNotification } from '~/hooks/useShowExtensionsNotification'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'

type SettingsNavItem = {
  title: React.ReactNode
  href: string
  prefix?: React.ReactElement
}

type SettingsNavSection = {
  title: string
  items: SettingsNavItem[]
}

const icon = (Icon: typeof Settings) => <Icon />

export const useSettingsNavSections = (): SettingsNavSection[] => {
  const { org: activeOrg, featureFlags } = useActiveOrg()
  const orgMember = useActiveOrgMember()
  const isOwner = orgMember?.role === 'OWNER'
  const isTeamOrg =
    !!activeOrg && !activeOrg.isPersonal && activeOrg.members?.length > 0
  const { smartAccounts } = useSmartAccounts({ showArchived: isOwner })
  const hasSmartAccounts = smartAccounts.length > 0
  const showExtensionsNotification = useShowExtensionsNotification()

  return compact([
    {
      title: activeOrg?.name ?? 'Workspace',
      items: compact([
        {
          title: 'General',
          href: '/settings/team/general',
          prefix: icon(Settings),
        },
        isTeamOrg && {
          title: 'Members',
          href: '/settings/team/members',
          prefix: icon(Users),
        },
        isOwner &&
          isTeamOrg &&
          hasSmartAccounts && {
            title: 'Networks',
            href: '/settings/team/networks',
            prefix: icon(Globe),
          },
        isTeamOrg &&
          !!featureFlags?.['gas-account'] && {
            title: 'Gas',
            href: '/settings/team/gas',
            prefix: icon(Fuel),
          },
        isOwner &&
          isTeamOrg &&
          hasSmartAccounts && {
            title: 'Recovery',
            href: '/settings/team/recovery',
            prefix: icon(Shield),
          },
        isOwner &&
          isTeamOrg && {
            title: 'Tokens',
            href: '/settings/team/tokens',
            prefix: icon(Coins),
          },
        isOwner &&
          isTeamOrg && {
            title: 'Banks',
            href: '/settings/team/banks',
            prefix: icon(Landmark),
          },
        isTeamOrg && {
          title: 'API Keys',
          href: '/settings/team/api-keys',
          prefix: icon(KeyRound),
        },
        !!featureFlags?.['accounting-categorization'] && {
          title: 'Accounting',
          href: '/settings/team/accounting',
          prefix: icon(BookOpen),
        },
        isTeamOrg && {
          title: 'Upgrade',
          href: '/settings/team/upgrade',
          prefix: icon(Gem),
        },
      ]),
    },
    {
      title: 'Personal',
      items: [
        {
          title: 'Profile',
          href: '/settings/personal/profile',
          prefix: icon(User),
        },
        {
          title: 'Signing keys',
          href: '/settings/personal/signing-keys',
          prefix: icon(Fingerprint),
        },
        {
          title: (
            <NotificationDotLabel show={showExtensionsNotification}>
              Extension
            </NotificationDotLabel>
          ),
          href: '/settings/personal/extensions',
          prefix: icon(Puzzle),
        },
        {
          title: 'Notifications',
          href: '/settings/personal/notifications',
          prefix: icon(BellDot),
        },
      ],
    },
  ])
}

export const SettingsSidebar = () => {
  const sections = useSettingsNavSections()

  return (
    <>
      {sections.map((section, i) => (
        <div key={section.title}>
          {i > 0 && (
            <hr className="border-gray-150 my-2 dark:border-gray-800" />
          )}
          <SidebarSection title={section.title} items={section.items} />
        </div>
      ))}
    </>
  )
}
